/**
 * BrowserToolExecutor
 *
 * Core handler logic for all browser automation tools.
 * Both the API and CLI instantiate this with their `IBrowserClient`
 * and call the same methods, ensuring identical behavior.
 *
 * @module
 */

import * as path from 'path';
import * as fs from 'fs/promises';
import * as crypto from 'crypto';
import type {
  IBrowserClient,
  MCPContentResult,
  MCPMediaContent,
  ProbeResult,
  RunCodeExecutionSignals,
  ScrollInfo,
  ScrollResult,
} from '../browser-client.interface.js';
import type { ClickedElementInfo } from '../click-validation.js';
import type {
  FastDriveScanInput,
  ScanStatus,
  FastDriveScanResult,
} from '../playwright-client/fast-drive-scan.js';
import { formatScanResultText } from '../playwright-client/fast-drive-scan-format.js';
import {
  shouldRenderScanVisual,
  scanHighlightBoxes,
} from '../playwright-client/fast-drive-scan-visual.js';
import type { ElementHintCategory, ElementHintSourceType } from '@chatsdet/types';
import { formatClickedElement } from '../click-validation.js';
import {
  extractSnapshotYaml,
  detectModalFromYaml,
  findCloseButton,
  parseSnapshot,
} from '../snapshot-analyzer.js';
import {
  downsampleImage,
  annotateScreenshotIndexed,
  cropImage,
  addCoordinateGridOverlay,
  MAX_DIMENSION,
  type BoundingBox,
} from '../image-utils.js';
import {
  expandSectionOnly,
  searchElements,
  findRelevanceScope,
  findActiveElement,
  type SearchMatch,
} from '../snapshot-formatter.js';
import { formatSnapshotMaybeOffloaded } from '../cpu-offload/index.js';
import {
  captureSnapshotState,
  classifyScreenshotWorthiness,
  compareSnapshots,
  formatSemanticDiff,
  type SnapshotState,
} from '../snapshot-diff.js';
import { getCanaryTmpDir } from '@chatsdet/tmp';
import { TIMEOUTS, LARGE_PAGE_REF_THRESHOLD } from '../constants.js';
import { combineSignals } from '../combine-signals.js';
import { ToolAbortedError } from '../tool-abort-error.js';
import type { BrowserLogger } from '../logger.js';
import {
  formatFillTargetNotEditableRecovery,
  isFillTargetNotEditableError,
} from '../fill-target-errors.js';
import {
  getMCPContentMedia,
  isMCPContentWithMedia,
  withRecoveryNotice,
  withRecoveryNoticeMCP,
  buildResolutionNote,
  ensureAllowedHost,
} from './helpers.js';
import {
  captureRunCodeBeforeObservation,
  collectRunCodePostActionSignals,
  formatImmediateRunCodeObservation,
  formatRunCodeResult,
  formatSettleRunCodeObservation,
} from './run-code-observation.js';
import { applyReflessSnapshotGate } from './refless-snapshot-gate.js';
import {
  formatNavigateDownloadStartingRecovery,
  isNavigateDownloadStartingError,
  resolveBrowserNavigateUrl,
} from './navigation-recovery.js';
import { formatScreenshotResult } from './screenshot-result.js';

import type { ElementHintAnnotation } from '../snapshot-formatter-shared.js';
import {
  normalizeUrlForHints,
  fingerprintForHint,
  type ElementFingerprint,
} from '../element-fingerprint.js';

export type ToolResult = string | MCPContentResult;

const BROWSER_WAIT_FOR_SELECTOR_STATES = ['visible', 'hidden', 'attached', 'detached'] as const;
type BrowserWaitForSelectorState = (typeof BROWSER_WAIT_FOR_SELECTOR_STATES)[number];
const BROWSER_WAIT_FOR_SELECTOR_STATE_SET = new Set<string>(BROWSER_WAIT_FOR_SELECTOR_STATES);

function isBrowserWaitForSelectorState(value: unknown): value is BrowserWaitForSelectorState {
  return typeof value === 'string' && BROWSER_WAIT_FOR_SELECTOR_STATE_SET.has(value);
}

function formatInvalidWaitForSelectorState(value: unknown): string {
  const rendered = typeof value === 'string' ? `"${value}"` : String(value);
  return `Invalid browser_wait_for state ${rendered}. Use one of: ${BROWSER_WAIT_FOR_SELECTOR_STATES.join(
    ', '
  )}.`;
}

function getFirstMCPMedia(result: MCPContentResult): MCPMediaContent | undefined {
  return getMCPContentMedia(result)[0];
}

function updateFirstMCPMediaData(result: MCPContentResult, data: string): void {
  const media = getMCPContentMedia(result);
  if (!media[0]) return;

  media[0].data = data;
  if (result.media?.[0] && result.media[0] !== media[0]) result.media[0].data = data;
  if (result.images?.[0] && result.images[0] !== media[0]) result.images[0].data = data;
}

const PRESS_KEY_ALIASES = new Map<string, string>([
  ['ctrl', 'Control'],
  ['cmd', 'Meta'],
  ['command', 'Meta'],
  ['option', 'Alt'],
  ['esc', 'Escape'],
  ['return', 'Enter'],
  ['spacebar', 'Space'],
  ['del', 'Delete'],
  ['left', 'ArrowLeft'],
  ['right', 'ArrowRight'],
  ['up', 'ArrowUp'],
  ['down', 'ArrowDown'],
]);

const ACTION_KEYS = new Set(['Enter', 'Escape', 'Tab', 'Space']);

function formatEmptyPressKeyRecovery(): string {
  return [
    'Error: browser_press_key requires a non-empty key name.',
    'Do not retry with an empty string. Use a Playwright key such as "Enter", "Escape", "Tab", "ArrowDown", or use browser_type when you need to enter text.',
  ].join(' ');
}

function normalizePressKey(key: string): string {
  return key
    .trim()
    .split('+')
    .map((part) => {
      const trimmed = part.trim();
      return PRESS_KEY_ALIASES.get(trimmed.toLowerCase()) ?? trimmed;
    })
    .join('+');
}

function normalizeRef(ref: string | undefined): string | undefined {
  const trimmed = ref?.trim();
  return trimmed ? trimmed : undefined;
}

function formatEmptyRefRecovery(toolName: string): string {
  return [
    `Error: ${toolName} requires a non-empty element ref from the latest browser_snapshot.`,
    'Do not retry with an empty string. Call browser_snapshot and use a concrete ref such as "e42", or use the coordinate path when that tool supports coordinates.',
  ].join(' ');
}

function extractWaitingAriaRef(error: unknown): string | undefined {
  const message = error instanceof Error ? error.message : String(error);
  return message.match(/waiting for locator\('aria-ref=([^']+)'\)/)?.[1];
}

function isStaleAriaRefActionTimeout(error: unknown, action: 'click' | 'fill'): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes(`${action}: Timeout`) &&
    message.includes("waiting for locator('aria-ref=") &&
    !message.includes('locator resolved to <')
  );
}

function formatStaleRefActionRecovery(toolName: string, ref: string, element?: string): string {
  const label = element ? ` for "${element}"` : '';
  return [
    `Error: ${toolName} could not find ref ${ref}${label} in the current page snapshot.`,
    'Do not retry the same ref. Call browser_snapshot and choose a fresh ref, or use the coordinate path when that tool supports coordinates.',
  ].join(' ');
}

function isSelectOptionNonSelectError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return message.includes('selectOption:') && message.includes('Element is not a <select>');
}

function isMissingDownloadTempFileError(message: string): boolean {
  return (
    /\bENOENT:\s+no such file or directory,\s+open\b/.test(message) &&
    message.includes('/playwright-downloads-')
  );
}

function getAbsoluteUrlProtocol(url: string): string | null {
  try {
    return new URL(url).protocol;
  } catch {
    return null;
  }
}

function formatUnsupportedPdfReadUrlRecovery(url: string, protocol: string): string {
  if (protocol === 'file:') {
    return [
      `Error: browser_pdf_read cannot fetch file: URLs (${url}).`,
      'If this PDF was downloaded in the current browser session, call browser_list_downloads to find the captured file, then use browser_read_download to read it.',
      'Use browser_pdf_read only for http(s) PDF URLs that the browser can request with its authenticated session.',
    ].join(' ');
  }
  return [
    `Error: browser_pdf_read cannot fetch ${protocol} URLs (${url}).`,
    'Use browser_pdf_read only for http(s) PDF URLs or browser-relative paths. For captured downloads, call browser_list_downloads and browser_read_download.',
  ].join(' ');
}

/** Derive a safe `.pdf` filename from a URL for staging into the Python workspace. */
function pdfWorkspaceFilename(url: string): string {
  let base = '';
  try {
    base = new URL(url, 'http://relative.invalid').pathname.split('/').pop() ?? '';
  } catch {
    base = url.split(/[?#]/)[0].split('/').pop() ?? '';
  }
  const safe = base
    .toLowerCase()
    .replace(/\.pdf$/i, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${safe || 'document'}.pdf`;
}

function formatSelectOptionNonSelectRecovery(ref: string, value: string, element: string): string {
  return [
    `Error: browser_select_option can only operate on native <select> elements; ref ${ref} for "${element}" resolved to a custom dropdown or combobox.`,
    `Do not retry browser_select_option for this element. Use browser_click to open the dropdown, then browser_click the option "${value}" from the menu. If the dropdown is searchable, click it and use browser_type before selecting the option.`,
  ].join(' ');
}

/**
 * Opaque span handle returned by the span factory.
 * Callers must pass this back to `endSpan` when the operation completes.
 */
export type SpanHandle = unknown;

/**
 * Callback interface for creating child spans within browser tool operations.
 * Keeps browser-core free of any tracing library dependency — the API layer
 * provides Sentry-backed implementations.
 */
export interface SpanFactory {
  startSpan(op: string, description: string): SpanHandle;
  endSpan(handle: SpanHandle, status?: 'ok' | 'error'): void;
  /** Attach flat key/value attributes to a span (optional; no-op if unsupported). */
  setAttributes?(handle: SpanHandle, attrs: Record<string, string | number | boolean>): void;
}

/**
 * Interface for fetching and managing element hints.
 * Implemented by the API layer using the DB repository;
 * CLI stubs this as a no-op.
 */
export interface HintProvider {
  /** Get all active hints for a property + normalized URL */
  getHintsForUrl(
    propertyId: string,
    urlNorm: string
  ): Promise<Array<ElementHintAnnotation & { fingerprintHash: string; body?: string | null }>>;

  /** Create or update a hint */
  createHint(opts: {
    propertyId: string;
    canonicalPageId?: string | null;
    urlNorm: string;
    fingerprintHash: string;
    fingerprint: ElementFingerprint;
    category: ElementHintCategory;
    element: string;
    frontmatter: string;
    body?: string | null;
    sourceType: ElementHintSourceType;
    sourceId?: string | null;
    sessionId?: string | null;
  }): Promise<{ id: string }>;

  /** Invalidate a hint by fingerprint */
  invalidateHint(opts: {
    propertyId: string;
    urlNorm: string;
    fingerprintHash: string;
    reason: string;
  }): Promise<void>;

  /** Get the full body text of a hint */
  getHintBody(hintId: string): Promise<string | null>;

  /** Record successful matches (batch) */
  recordMatches(hintIds: string[]): Promise<void>;
}

export interface HintResult {
  message: string;
  created?: {
    id: string;
    frontmatter: string;
    category: string;
    fingerprintHash: string;
    urlNorm: string;
  };
}

export interface BrowserToolOptions {
  signal?: AbortSignal;
  onScreenshot?: (image: string, label?: string) => Promise<void>;
  maxImageDimension?: number;
  clickValidation?: boolean;
  allowedHosts?: string[];
  /** Include screenshot alongside accessibility tree in snapshots (default: true) */
  includeScreenshotWithSnapshot?: boolean;
  /**
   * Live-snapshot path: warm/prepared snapshots reused when the raw substrate
   * (ariaSnapshot) is unchanged, plus explicit browser_snapshot auto-mode
   * screenshot decisions. Default false → exact v1 behavior. Gated per-org by
   * the `browser.live-snapshot` flag.
   */
  liveSnapshot?: boolean;
  /**
   * On a freshness miss, return the raw substrate immediately and augment in the
   * background instead of blocking on augmentation. Default false → block (full
   * formatted snapshot, matching v1). The raw path is a degraded (v1-ish) format.
   */
  liveSnapshotRawOnMiss?: boolean;
  /**
   * Ref-less (fast-drive) tool surface. When true, the client skips its forced
   * post-navigation snapshot and warmSnapshot() is a no-op — the ref-less agent
   * re-perceives on its next turn, so those captures are wasted work. Set true
   * only by createMinimalBrowserTools; ref-based sets leave it false.
   */
  reflessSnapshots?: boolean;
  workspaceDir?: string;
  /**
   * Base directory for temporary files. Used as the base for auto-generated workspace dirs.
   * API callers should pass an org-scoped directory.
   */
  tmpBaseDir?: string;
  logger?: BrowserLogger;
  /** Optional span factory for sub-operation tracing (Sentry, OpenTelemetry, etc.) */
  spanFactory?: SpanFactory;
  /** Optional hint provider for element hint injection into snapshots */
  hintProvider?: HintProvider;
  /** Property ID for scoping element hints — required if hintProvider is set */
  propertyId?: string;
  /**
   * Override the post-action snapshot timeout (ms). Defaults to
   * TIMEOUTS.POST_ACTION_SNAPSHOT (15s). Test seam — production callers
   * should leave this unset.
   */
  postActionSnapshotTimeoutMs?: number;
  /**
   * After this many consecutive post-action snapshot timeouts, the executor
   * upgrades the recovery hint from "[WARN] try X or Y" to a structured
   * "[PAGE WEDGED]" message with a single recommended next move
   * (browser_navigate to the last known URL). Default: 2.
   */
  pageWedgedThreshold?: number;
  /**
   * After this many consecutive post-action snapshot timeouts, the executor
   * stamps the result "[SESSION UNRECOVERABLE]" and instructs the agent to
   * fail the step rather than iterate further. Default: 4.
   */
  sessionUnrecoverableThreshold?: number;
}

/**
 * Extract refs from the final semantic snapshot text, not the raw YAML. The
 * formatter collapses and filters large parts of dense pages, so using this
 * list keeps DOM-side enrichment work proportional to what the agent will
 * actually see.
 */
export function extractFormattedSnapshotRefs(text: string): string[] {
  const refs = new Set<string>();
  for (const match of text.matchAll(/\[(e\d+)\]/g)) {
    refs.add(match[1]);
  }
  return [...refs];
}

/**
 * Parse `~hclip=left|right` markers (stamped by the per-ref augmentation pass)
 * out of the raw substrate YAML into a ref→direction map. We only keep the
 * horizontal-clip signal — vertical-below-fold is noise on any long list, and
 * scroll-down is the agent's default gesture anyway.
 */
export function parseHorizontalScrollRefs(yaml: string): Map<string, 'left' | 'right'> {
  const byRef = new Map<string, 'left' | 'right'>();
  for (const line of yaml.split('\n')) {
    const refMatch = line.match(/\[ref=(e\d+)\]/);
    if (!refMatch) continue;
    const dirMatch = line.match(/~hclip=(left|right)\b/);
    if (dirMatch) byRef.set(refMatch[1], dirMatch[1] as 'left' | 'right');
  }
  return byRef;
}

/**
 * Annotate horizontally-clipped refs in the formatted snapshot text with
 * `[scroll-left]` / `[scroll-right]` so the agent knows to scroll that way to
 * reach the element (e.g. a column off the right edge of a wide grid).
 */
export function markHorizontalScrollRefsInText(
  text: string,
  byRef: Map<string, 'left' | 'right'>
): string {
  if (byRef.size === 0) return text;
  return text.replace(/\[(e\d+)\](?! \[scroll-(?:left|right)\])/g, (match, ref) => {
    const dir = byRef.get(ref);
    return dir ? `[${ref}] [scroll-${dir}]` : match;
  });
}

/**
 * Core browser tool handler logic.
 *
 * Encapsulates all behavior: semantic diffs, snapshot formatting,
 * click validation, recovery notices, etc.
 *
 * Both the API adapter and CLI MCP tools delegate to this class.
 */
export class BrowserToolExecutor {
  private client: IBrowserClient;
  private signal?: AbortSignal;
  /**
   * Optional per-tool-call AbortSignal. Set by `withCallSignal` while a tool
   * call is executing so an in-flight Playwright op aborts when the per-tool
   * timeout fires (instead of running orphaned). Combined with `this.signal`
   * (the long-lived session signal) at every client call site via
   * `this.currentSignal`. See `withCallSignal` below.
   */
  private callSignal?: AbortSignal;
  /**
   * Resolves when the current `withCallSignal` invocation has restored
   * executor state. A timed-out browser tool can return a recovery result
   * before the underlying Playwright operation unwinds; the next tool waits on
   * this instead of throwing a generic concurrency invariant into Sentry.
   */
  private activeCallSettled?: Promise<void>;
  /**
   * Cached combined-signal for the current `withCallSignal` scope. Populated
   * once on entry and reset on exit so the ~45 `currentSignal` reads per tool
   * call share one allocation instead of calling `combineSignals` each time.
   */
  private cachedCombinedSignal?: AbortSignal;
  private onScreenshot?: (image: string, label?: string) => Promise<void>;
  private maxImageDimension: number;
  private clickValidation: boolean;
  private allowedHosts?: string[];
  private includeScreenshotWithSnapshot: boolean;
  private liveSnapshotEnabled: boolean;
  private explicitSnapshotBaseline: { url: string; yaml: string; state: SnapshotState } | null =
    null;
  private workspaceDir: string;
  private logger?: BrowserLogger;
  private spanFactory?: SpanFactory;
  private hintProvider?: HintProvider;
  private propertyId?: string;
  private postActionSnapshotTimeoutMs: number;
  private pageWedgedThreshold: number;
  private sessionUnrecoverableThreshold: number;
  /**
   * Consecutive post-action snapshot timeouts in the executor's lifetime.
   * Reset on a successful capture. Drives the "[PAGE WEDGED]" /
   * "[SESSION UNRECOVERABLE]" escalation so the agent isn't left to do
   * cross-call bookkeeping itself (which is what burned thoughts 24-40 of
   * prod run 89d912d1).
   */
  private consecutiveSnapshotTimeouts = 0;
  /**
   * The most recent click/type/pressKey, captured so `handleDialog` with
   * `replayLastAction:true` can re-issue it after pre-arming the dialog
   * listener. Stored as a thunk closing over the original opts so the replay
   * uses the exact same descriptor (ref, coords, text) — avoids the drift the
   * old two-step "accept then click again yourself" contract was prone to
   * (toast appearing between clicks, focus shifting, table re-rendering).
   *
   * If the page has navigated since, the underlying ref/coords will fail to
   * resolve and the agent gets a clean error — we don't try to be clever.
   */
  private lastReplayableAction: {
    description: string;
    thunk: () => Promise<ToolResult>;
  } | null = null;
  /** Per-session cache of hints by normalized URL */
  private hintCache = new Map<string, Map<string, ElementHintAnnotation>>();

  constructor(client: IBrowserClient, options?: BrowserToolOptions) {
    this.client = client;
    this.signal = options?.signal;
    this.onScreenshot = options?.onScreenshot;
    this.maxImageDimension = options?.maxImageDimension ?? MAX_DIMENSION;
    this.clickValidation = options?.clickValidation ?? false;
    this.allowedHosts = options?.allowedHosts;
    this.includeScreenshotWithSnapshot = options?.includeScreenshotWithSnapshot !== false;
    this.liveSnapshotEnabled = options?.liveSnapshot === true;
    // Propagate the gate to the client so its snapshot()/warm path matches.
    this.client.setLiveSnapshotEnabled?.(this.liveSnapshotEnabled);
    this.client.setLiveSnapshotRawOnMiss?.(options?.liveSnapshotRawOnMiss === true);
    applyReflessSnapshotGate(this.client, options);
    if (!options?.workspaceDir && !options?.tmpBaseDir) {
      options?.logger?.debug?.(
        '[BrowserToolExecutor] No tmpBaseDir provided, using flat canary dir (not org-scoped)'
      );
    }
    this.workspaceDir =
      options?.workspaceDir ??
      path.join(
        options?.tmpBaseDir ?? getCanaryTmpDir(),
        `browser-workspace-${Date.now()}-${crypto.randomUUID().slice(0, 8)}`
      );
    this.logger = options?.logger;
    this.spanFactory = options?.spanFactory;
    this.hintProvider = options?.hintProvider;
    this.propertyId = options?.propertyId;
    this.postActionSnapshotTimeoutMs =
      options?.postActionSnapshotTimeoutMs ?? TIMEOUTS.POST_ACTION_SNAPSHOT;
    this.pageWedgedThreshold = options?.pageWedgedThreshold ?? 2;
    this.sessionUnrecoverableThreshold = options?.sessionUnrecoverableThreshold ?? 4;

    // Ensure workspace directory exists
    fs.mkdir(this.workspaceDir, { recursive: true }).catch(() => {});
  }

  /**
   * Get element hints for the current URL, using the per-session cache.
   * Returns a Map keyed by fingerprint hash, ready for formatSemanticSnapshot options.
   * Returns undefined if no hint provider is configured or the property context is missing.
   */
  private async getElementHintsForSnapshot(
    propertyId: string | undefined,
    urlNorm: string | undefined
  ): Promise<Map<string, ElementHintAnnotation> | undefined> {
    if (!this.hintProvider || !propertyId || !urlNorm) return undefined;

    // Check cache first
    const cached = this.hintCache.get(urlNorm);
    if (cached) return cached.size > 0 ? cached : undefined;

    try {
      const hints = await this.hintProvider.getHintsForUrl(propertyId, urlNorm);
      const map = new Map<string, ElementHintAnnotation>();
      for (const hint of hints) {
        map.set(hint.fingerprintHash, {
          id: hint.id,
          category: hint.category,
          element: hint.element,
          frontmatter: hint.frontmatter,
          hasBody: hint.body !== null && hint.body !== undefined,
        });
      }
      this.hintCache.set(urlNorm, map);
      return map.size > 0 ? map : undefined;
    } catch (err) {
      this.logger?.debug?.('[BrowserToolExecutor] Failed to fetch element hints', {
        error: err instanceof Error ? err.message : String(err),
      });
      return undefined;
    }
  }

  /** Clear the hint cache for a specific URL (after create/invalidate) */
  private clearHintCache(urlNorm?: string): void {
    if (urlNorm) {
      this.hintCache.delete(urlNorm);
    } else {
      this.hintCache.clear();
    }
  }

  /**
   * Run an async operation wrapped in a tracing span (if spanFactory is configured).
   */
  private async traced<T>(op: string, description: string, fn: () => Promise<T>): Promise<T> {
    if (!this.spanFactory) return fn();
    const handle = this.spanFactory.startSpan(op, description);
    try {
      const result = await fn();
      this.spanFactory.endSpan(handle, 'ok');
      return result;
    } catch (err) {
      this.spanFactory.endSpan(handle, 'error');
      throw err;
    }
  }

  /** Get the workspace directory path */
  getWorkspaceDir(): string {
    return this.workspaceDir;
  }

  /**
   * Combined signal of `this.signal` (session-lifetime) and `this.callSignal`
   * (per-tool-call timeout). Read at every client call site. Memoized via
   * `cachedCombinedSignal` so a single tool call doesn't allocate ~45 combined
   * signals via `combineSignals`.
   */
  private get currentSignal(): AbortSignal | undefined {
    return this.cachedCombinedSignal ?? this.signal;
  }

  /**
   * Run a tool-handler invocation with a per-call AbortSignal in effect.
   * Combines with the constructor signal at every client call site. On abort,
   * calls `client.attemptUnwedgePage()` (when implemented) to release pending
   * fetch/XHR loads so the next tool sees a responsive page.
   */
  async withCallSignal<T>(signal: AbortSignal | undefined, fn: () => Promise<T>): Promise<T> {
    if (this.callSignal !== undefined && this.callSignal !== signal) {
      const activeCallSettled = this.activeCallSettled;
      if (activeCallSettled) {
        await this.waitForActiveCallToSettle(activeCallSettled, signal);
      }

      if (this.callSignal !== undefined && this.callSignal !== signal) {
        throw new ToolAbortedError('withCallSignal:previous-call-active');
      }
    }

    const prev = this.callSignal;
    const prevCached = this.cachedCombinedSignal;
    const prevActiveCallSettled = this.activeCallSettled;
    let resolveCurrentCall: (() => void) | undefined;
    const currentCallSettled = new Promise<void>((resolve) => {
      resolveCurrentCall = resolve;
    });
    this.callSignal = signal;
    this.activeCallSettled = currentCallSettled;
    this.cachedCombinedSignal = signal ? combineSignals(this.signal, signal) : this.signal;

    const fireUnwedge = (): void => {
      const client = this.client;
      if (typeof client.attemptUnwedgePage !== 'function') return;
      try {
        // Best-effort, non-destructive: window.stop() cancels a hung load so a
        // slow page can make progress. It does NOT close or recreate the page.
        client.attemptUnwedgePage();
      } catch (err) {
        // attemptUnwedgePage is documented as never-rejecting; belt-and-braces.
        this.logger?.warn?.('[BrowserToolExecutor] attemptUnwedgePage threw synchronously', {
          error: err instanceof Error ? err.message : String(err),
        });
      }
      // We intentionally do NOT force-recover (close + rebuild the context) on a
      // per-tool timeout. The old liveness-gated recovery used a 750ms probe that
      // read a busy-but-alive heavy page as "unresponsive" and tore it down,
      // dropping auth and storming wire-teardown errors (prod SEV1, June 2026).
      // A timeout just aborts the tool and returns a recovery string to the agent;
      // the page is left intact. Genuine wedges are reaped by the page crash/close
      // events and the ~28-min step-deadline backstop.
      // TODO(browser-recovery): reintroduce a reliable mid-run recovery for a
      // genuinely-wedged-but-open page that does not mistake slowness for death.
    };

    // Pre-aborted entry: fire the unwedge once and bail before invoking `fn`
    // so we don't run an action against a page we just stop()-bombed.
    if (signal?.aborted) {
      fireUnwedge();
      this.callSignal = prev;
      resolveCurrentCall?.();
      this.activeCallSettled = prevActiveCallSettled;
      this.cachedCombinedSignal = prevCached;
      this.logger?.info?.('[BrowserToolExecutor] tool call entered with already-aborted signal');
      throw new ToolAbortedError('withCallSignal:pre-aborted');
    }

    let onAbort: (() => void) | undefined;
    if (signal) {
      onAbort = (): void => fireUnwedge();
      signal.addEventListener('abort', onAbort, { once: true });
    }

    try {
      return await fn();
    } catch (err) {
      if (err instanceof ToolAbortedError) {
        this.logger?.info?.('[BrowserToolExecutor] tool aborted', { error: err.message });
      }
      throw err;
    } finally {
      this.callSignal = prev;
      resolveCurrentCall?.();
      this.activeCallSettled = prevActiveCallSettled;
      this.cachedCombinedSignal = prevCached;
      if (onAbort && signal) signal.removeEventListener('abort', onAbort);
    }
  }

  private async waitForActiveCallToSettle(
    activeCallSettled: Promise<void>,
    signal: AbortSignal | undefined
  ): Promise<void> {
    if (!signal) {
      await activeCallSettled.catch(() => {});
      return;
    }

    if (signal.aborted) {
      throw new ToolAbortedError('withCallSignal:wait-for-active-call');
    }

    let onAbort: (() => void) | undefined;
    const abortPromise = new Promise<never>((_, reject) => {
      onAbort = () => reject(new ToolAbortedError('withCallSignal:wait-for-active-call'));
      signal.addEventListener('abort', onAbort, { once: true });
    });

    try {
      await Promise.race([activeCallSettled.catch(() => {}), abortPromise]);
    } finally {
      if (onAbort) signal.removeEventListener('abort', onAbort);
    }
  }

  // NOTE: the per-tool-timeout / post-action-snapshot "liveness-gated forced
  // recovery" (scheduleForceRecover / maybeForceRecover) was removed June 2026.
  // Its 750ms liveness probe read busy-but-alive heavy pages as "unresponsive"
  // and tore them down, dropping the authenticated session and storming
  // Playwright wire-teardown errors (prod SEV1). Slowness is no longer treated as
  // a wedge: tools time out into a recovery string, the page is left intact, and
  // genuine page death is handled by the crash/close events and the step-deadline
  // backstop. See TODO(browser-recovery) in withSemanticDiff / fireUnwedge.

  // ==================== Navigation ====================

  async navigate(url: string): Promise<ToolResult> {
    const navigationUrl = await resolveBrowserNavigateUrl(url, () =>
      this.client.getCurrentUrl({ signal: this.currentSignal })
    );
    if (navigationUrl.kind === 'recovery') {
      return navigationUrl.message;
    }
    ensureAllowedHost(navigationUrl.url, this.allowedHosts);
    let rawResult: string;
    try {
      rawResult = await this.client.navigate(navigationUrl.url, { signal: this.currentSignal });
    } catch (error) {
      if (isNavigateDownloadStartingError(error)) {
        return formatNavigateDownloadStartingRecovery();
      }
      throw error;
    }
    const result = withRecoveryNotice(this.client, rawResult, this.logger);
    return this.formatNavigateResult(result);
  }

  async navigateBack(): Promise<ToolResult> {
    const result = await this.client.navigateBack({ signal: this.currentSignal });
    return this.formatNavigateResult(result);
  }

  // ==================== Inspection ====================

  async snapshot(opts: {
    mode?: 'auto' | 'both' | 'screenshot' | 'tree' | 'tree-refless';
    expand?: string;
    search?: string;
    showCoordinateGrid?: boolean;
    probeAt?: { x: number; y: number };
    hintDetail?: string;
  }): Promise<ToolResult> {
    if (opts.probeAt?.x === 0 && opts.probeAt?.y === 0) {
      return (
        'Error: browser_snapshot probeAt=(0, 0) is invalid. ' +
        'The coordinates were likely defaulted instead of intentionally chosen. ' +
        'Call browser_snapshot without probeAt for page context, or provide real coordinates before retrying.'
      );
    }

    const legacyDefaultMode: 'both' | 'tree' = this.includeScreenshotWithSnapshot ? 'both' : 'tree';
    const effectiveMode: 'auto' | 'both' | 'screenshot' | 'tree' | 'tree-refless' =
      opts.mode === 'auto'
        ? this.liveSnapshotEnabled
          ? 'auto'
          : legacyDefaultMode
        : (opts.mode ?? (this.liveSnapshotEnabled ? 'auto' : legacyDefaultMode));
    const clientSnapshotMode = effectiveMode === 'auto' ? 'tree' : effectiveMode;

    const effectiveShowCoordinateGrid = opts.showCoordinateGrid ?? effectiveMode === 'screenshot';

    let result = await this.traced('snapshot', 'client.snapshot', () =>
      this.client.snapshot({
        signal: this.currentSignal,
        mode: clientSnapshotMode,
        maxImageDimension: this.maxImageDimension,
        // Ref-less mode filters in the client renderer; ref-based modes ignore
        // this and run their own search machinery below.
        search: clientSnapshotMode === 'tree-refless' ? opts.search : undefined,
      })
    );

    let hasImages = isMCPContentWithMedia(result);
    const rawTextContent = hasImages
      ? ((result as MCPContentResult).text ?? '')
      : typeof result === 'string'
        ? result
        : String(result);

    // Ref-less (fast-drive) mode: the client already rendered the final semantic
    // snapshot via the dedicated ref-less formatter (the shared semantic
    // formatter gates on refs and would emit nothing). None of the ref-based
    // machinery below (search/probe/hclip/hints) applies — pass it through.
    if (effectiveMode === 'tree-refless') {
      return withRecoveryNoticeMCP(this.client, rawTextContent, this.logger);
    }

    // Parse search terms
    const searchTerms = opts.search
      ? opts.search
          .split('|')
          .map((t) => t.trim())
          .filter(Boolean)
      : [];

    this.logger?.debug?.('[browser_snapshot] Raw result captured', {
      mode: effectiveMode,
      clientSnapshotMode,
      resultType: hasImages ? 'MCPContentResult' : 'string',
      textCharCount: rawTextContent.length,
    });

    // Screenshot-only mode
    if (effectiveMode === 'screenshot') {
      if (hasImages) {
        const viewportSize = this.client.getViewportSize();
        const maxImageDim = this.maxImageDimension;
        const maxDim = Math.max(viewportSize.width, viewportSize.height);
        const scale = maxDim > maxImageDim ? maxImageDim / maxDim : 1;
        const imgWidth = Math.round(viewportSize.width * scale);
        const imgHeight = Math.round(viewportSize.height * scale);

        const scaleInfo =
          `[Screenshot Mode]\n` +
          `Viewport: ${viewportSize.width}x${viewportSize.height} | Image: ~${imgWidth}x${imgHeight} | Scale: ${scale.toFixed(2)}\n` +
          buildResolutionNote(viewportSize, maxImageDim, this.logger) +
          '\n' +
          `${effectiveShowCoordinateGrid ? 'Coordinate grid enabled - read coordinates from grid labels.\n' : ''}` +
          `Click coordinates should be in viewport space (0-${viewportSize.width}, 0-${viewportSize.height}).\n`;

        return withRecoveryNoticeMCP(
          this.client,
          {
            text: scaleInfo + (rawTextContent || ''),
            media: getMCPContentMedia(result as MCPContentResult),
            images:
              (result as MCPContentResult).images ?? getMCPContentMedia(result as MCPContentResult),
          },
          this.logger
        );
      }
      return withRecoveryNoticeMCP(this.client, rawTextContent, this.logger);
    }

    // Apply semantic formatting
    const yaml = extractSnapshotYaml(rawTextContent);
    const currentUrl = await this.client.getCurrentUrl({ signal: this.currentSignal });
    const currentState = captureSnapshotState(currentUrl, yaml);

    if (effectiveMode === 'auto') {
      let screenshotReason: string | undefined;
      if (opts.showCoordinateGrid === true) {
        screenshotReason = 'coordinate-grid';
      } else if (!this.explicitSnapshotBaseline) {
        screenshotReason = 'first-snapshot';
      } else {
        const diff = compareSnapshots(this.explicitSnapshotBaseline.state, currentState);
        const worthiness = classifyScreenshotWorthiness(diff);
        screenshotReason = worthiness.warranted ? worthiness.reason : undefined;
      }

      if (screenshotReason) {
        try {
          const screenshotResult = await this.traced(
            'snapshot_auto_screenshot',
            'auto snapshot screenshot',
            () =>
              this.client.snapshot({
                signal: this.currentSignal,
                mode: 'screenshot',
                maxImageDimension: this.maxImageDimension,
              })
          );
          if (isMCPContentWithMedia(screenshotResult)) {
            const media = getMCPContentMedia(screenshotResult);
            result = {
              text: rawTextContent,
              media,
              images: (screenshotResult as MCPContentResult).images ?? media,
            };
            hasImages = true;
          }
        } catch (e) {
          this.logger?.warn?.('[browser_snapshot] Auto screenshot capture failed', {
            reason: screenshotReason,
            error: e instanceof Error ? e.message : String(e),
          });
        }
      }

      this.client.recordLiveSnapshotEvent?.({
        kind: 'screenshot',
        reason: hasImages ? screenshotReason : undefined,
      });
    } else if (this.liveSnapshotEnabled && effectiveMode === 'both') {
      this.client.recordLiveSnapshotEvent?.({
        kind: 'screenshot',
        reason: hasImages ? 'explicit-both' : undefined,
      });
    }

    this.explicitSnapshotBaseline = { url: currentUrl, yaml, state: currentState };

    // Coordinate grid overlay
    if (effectiveShowCoordinateGrid && hasImages) {
      try {
        await this.traced('coordinate_grid', 'coordinate grid overlay', async () => {
          const viewportSize = this.client.getViewportSize();
          const mediaResult = result as MCPContentResult;
          const media = getFirstMCPMedia(mediaResult);
          if (!media) return;
          const imageWithGrid = await addCoordinateGridOverlay(media.data, viewportSize);
          updateFirstMCPMediaData(mediaResult, imageWithGrid);
        });
      } catch (e) {
        this.logger?.warn?.('[browser_snapshot] Failed to apply coordinate grid overlay', {
          error: e instanceof Error ? e.message : String(e),
        });
      }
    }

    // Emit screenshot to browser stream after any auto capture/grid overlay.
    if (hasImages && this.onScreenshot) {
      const media = getFirstMCPMedia(result as MCPContentResult);
      if (media) {
        await this.onScreenshot(`data:${media.mimeType};base64,${media.data}`, 'snapshot');
      }
    }

    // Expand section
    if (opts.expand) {
      const semanticText = expandSectionOnly(yaml, opts.expand);
      if (hasImages) {
        const resolutionNote = buildResolutionNote(
          this.client.getViewportSize(),
          this.maxImageDimension,
          this.logger
        );
        return withRecoveryNoticeMCP(
          this.client,
          {
            text: resolutionNote + '\n' + semanticText,
            media: getMCPContentMedia(result as MCPContentResult),
            images:
              (result as MCPContentResult).images ?? getMCPContentMedia(result as MCPContentResult),
          },
          this.logger
        );
      }
      return withRecoveryNoticeMCP(this.client, semanticText, this.logger);
    }

    // Search
    let searchMatches: SearchMatch[] = [];
    let totalMatchCount = 0;
    if (searchTerms.length > 0) {
      await this.traced('search_annotation', `search: ${searchTerms.join(', ')}`, async () => {
        const elements = parseSnapshot(yaml);
        const activeElement = findActiveElement(elements);
        const scopeRef = findRelevanceScope(elements, activeElement?.ref);
        const searchResult = searchElements(elements, searchTerms, scopeRef);
        searchMatches = searchResult.matches;
        totalMatchCount = searchResult.totalFound;

        if (searchMatches.length > 0) {
          // Assign indices first
          for (let i = 0; i < searchMatches.length; i++) {
            searchMatches[i].index = i + 1;
          }

          // Batch-query all bounding boxes in a single round-trip
          const matchRefs = searchMatches.map((m) => m.ref);
          const boxes = this.client.getElementBoundingBoxes
            ? await this.client.getElementBoundingBoxes(matchRefs, { signal: this.currentSignal })
            : new Map<string, { x: number; y: number; width: number; height: number }>();

          // Fallback: if batch method not available, query individually
          if (!this.client.getElementBoundingBoxes) {
            for (const match of searchMatches) {
              try {
                const box = await this.client.getElementBoundingBox(match.ref, {
                  signal: this.currentSignal,
                });
                if (box) boxes.set(match.ref, box);
              } catch {
                /* Element may not be visible */
              }
            }
          }

          for (const match of searchMatches) {
            const box = boxes.get(match.ref);
            if (box) {
              match.boundingBox = box;
              match.centerX = Math.round(box.x + box.width / 2);
              match.centerY = Math.round(box.y + box.height / 2);
            }
          }

          // Annotate screenshot
          if (hasImages) {
            const annotations = searchMatches
              .filter((m) => m.boundingBox && m.index)
              .map((m) => ({ index: m.index!, box: m.boundingBox! }));

            if (annotations.length > 0) {
              try {
                const viewportSize = this.client.getViewportSize();
                const mediaResult = result as MCPContentResult;
                const media = getFirstMCPMedia(mediaResult);
                if (!media) return;
                const annotatedImage = await annotateScreenshotIndexed(media.data, annotations, {
                  originalDimensions: viewportSize,
                });
                updateFirstMCPMediaData(mediaResult, annotatedImage);
              } catch (e) {
                this.logger?.warn?.('[browser_snapshot] Failed to annotate screenshot', {
                  error: e instanceof Error ? e.message : String(e),
                });
              }
            }
          }
        }
      });
    }

    // Probe at coordinates
    let probeResult: ProbeResult | undefined;
    if (opts.probeAt && searchTerms.length === 0 && this.client.probeAtCoordinates) {
      try {
        const probe = await this.client.probeAtCoordinates(opts.probeAt.x, opts.probeAt.y, {
          signal: this.currentSignal,
        });
        if (probe) {
          probeResult = probe;
          if (hasImages && probe.boundingBox) {
            try {
              const viewportSize = this.client.getViewportSize();
              const mediaResult = result as MCPContentResult;
              const media = getFirstMCPMedia(mediaResult);
              if (media) {
                const annotatedImage = await annotateScreenshotIndexed(
                  media.data,
                  [{ index: 1, box: probe.boundingBox }],
                  { originalDimensions: viewportSize }
                );
                updateFirstMCPMediaData(mediaResult, annotatedImage);
              }
            } catch {
              // Annotation failure is non-critical
            }
          }
        }
      } catch (e) {
        this.logger?.warn?.('[browser_snapshot] Probe at coordinates failed', {
          x: opts.probeAt.x,
          y: opts.probeAt.y,
          error: e instanceof Error ? e.message : String(e),
        });
      }
    }

    // Write snapshot to workspace
    let snapshotFilePath: string | undefined;
    try {
      await fs.mkdir(this.workspaceDir, { recursive: true });
      const filename = `snapshot-${Date.now()}-${crypto.randomUUID().slice(0, 8)}.yaml`;
      snapshotFilePath = path.join(this.workspaceDir, filename);
      await fs.writeFile(snapshotFilePath, yaml, 'utf-8');
    } catch {
      // Workspace write failure is non-critical
    }

    const urlNorm = currentUrl ? normalizeUrlForHints(currentUrl) : undefined;
    const elementHints = await this.getElementHintsForSnapshot(this.propertyId, urlNorm);

    const snapshotOpts = {
      searchMatches: searchMatches.length > 0 ? searchMatches : undefined,
      totalMatchCount: totalMatchCount > 0 ? totalMatchCount : undefined,
      snapshotFilePath,
      probeResult,
      currentTime: new Date().toISOString(),
      capturedToasts: this.client.getCapturedToasts?.() ?? [],
      consoleErrors: this.client.getNewConsoleErrors?.() ?? [],
      failedRequests: this.client.getNewFailedRequests?.() ?? [],
      elementHints,
      matchedHintIds: undefined as string[] | undefined,
    };
    let semanticText = await formatSnapshotMaybeOffloaded(yaml, snapshotOpts);

    // Detect off-screen elements by batch-querying only refs present in the
    // final semantic output. Raw snapshots can contain hundreds of refs inside
    // collapsed grids/sections; resolving those via `aria-ref=` is pure waste.
    // This keeps the Playwright selector-engine work proportional to the text
    // the agent receives while preserving the same visible annotations.
    // Off-screen handling (WS3): the per-ref augmentation pass already resolves
    // every ref and reads its bounding rect, so horizontal-clip detection rides
    // that existing pass for free (stamped as `~hclip=left|right` on the raw
    // substrate) — no second `aria-ref=` resolve + forced-layout scan. We only
    // surface the HORIZONTAL signal as `[scroll-left]`/`[scroll-right]`: it's the
    // non-obvious one (a column off the right of a wide grid). Vertical-below-fold
    // is noise on any long list and dropped entirely.
    //
    // On a very large page the augmentation skips hclip (see planChecks gate);
    // there we emit a [Large page] marker instead so the agent narrows/scrolls.
    const formattedRefs = extractFormattedSnapshotRefs(semanticText);
    if (formattedRefs.length > LARGE_PAGE_REF_THRESHOLD) {
      semanticText =
        `[Large page: ~${formattedRefs.length.toLocaleString()} interactive elements — ` +
        `scroll-position hints reduced for speed. Narrow with browser_snapshot search=… ` +
        `or scroll to a region.]\n` +
        semanticText;
    } else {
      const hScrollRefs = parseHorizontalScrollRefs(yaml);
      if (hScrollRefs.size > 0) {
        semanticText = markHorizontalScrollRefsInText(semanticText, hScrollRefs);
      }
    }

    if (
      snapshotOpts.matchedHintIds &&
      snapshotOpts.matchedHintIds.length > 0 &&
      this.hintProvider
    ) {
      this.hintProvider.recordMatches(snapshotOpts.matchedHintIds).catch(() => {
        // Non-critical — don't block snapshot delivery
      });
    }

    let hintDetailSection = '';
    if (opts.hintDetail && this.hintProvider && elementHints) {
      const fpResult = fingerprintForHint(parseSnapshot(yaml), opts.hintDetail);
      const matchedHint = fpResult ? elementHints.get(fpResult.hash) : undefined;
      if (matchedHint) {
        try {
          const body = await this.hintProvider.getHintBody(matchedHint.id);
          if (body) {
            hintDetailSection = `\n\n--- HINT DETAIL for [${opts.hintDetail}] ---\n${body}\n---`;
          }
        } catch {
          // Non-critical
        }
      }
    }

    if (hasImages) {
      const resolutionNote = buildResolutionNote(
        this.client.getViewportSize(),
        this.maxImageDimension,
        this.logger
      );
      return withRecoveryNoticeMCP(
        this.client,
        {
          text: resolutionNote + '\n' + semanticText + hintDetailSection,
          media: getMCPContentMedia(result as MCPContentResult),
          images:
            (result as MCPContentResult).images ?? getMCPContentMedia(result as MCPContentResult),
        },
        this.logger
      );
    }

    return withRecoveryNoticeMCP(this.client, semanticText + hintDetailSection, this.logger);
  }

  /**
   * Create, update, or invalidate an element hint.
   * Returns a structured result with the message and optional created hint metadata.
   */
  async hint(opts: {
    ref: string;
    category?: ElementHintCategory;
    element?: string;
    frontmatter?: string;
    body?: string;
    invalidate?: boolean;
    reason?: string;
  }): Promise<HintResult> {
    const ref = normalizeRef(opts.ref);
    if (!ref) {
      return { message: formatEmptyRefRecovery('browser_hint') };
    }

    if (!this.hintProvider) {
      return { message: 'Element hints are not available in this environment.' };
    }

    const currentUrl = await this.client.getCurrentUrl();
    const urlNorm = currentUrl ? normalizeUrlForHints(currentUrl) : undefined;
    if (!urlNorm || !this.propertyId) {
      return { message: 'Cannot create hint: missing URL or property context.' };
    }

    const lastYaml = this.client.getLastSnapshotIfFresh?.(30000);
    if (!lastYaml) {
      return {
        message: 'Cannot create hint: no recent snapshot available. Take a snapshot first.',
      };
    }
    const elements = parseSnapshot(lastYaml);

    const fpResult = fingerprintForHint(elements, ref);
    if (!fpResult) {
      return {
        message: `Cannot create hint: element ref "${ref}" not found in the current snapshot.`,
      };
    }

    if (opts.invalidate) {
      if (!opts.reason) {
        return { message: 'Cannot invalidate hint: reason is required.' };
      }
      await this.hintProvider.invalidateHint({
        propertyId: this.propertyId,
        urlNorm,
        fingerprintHash: fpResult.hash,
        reason: opts.reason,
      });
      this.clearHintCache(urlNorm);
      this.logger?.debug?.('[browser_hint] Hint invalidated', {
        propertyId: this.propertyId,
        urlNorm,
        fingerprintHash: fpResult.hash,
        reason: opts.reason,
      });
      return { message: `Hint on [${ref}] invalidated: ${opts.reason}` };
    }

    if (!opts.frontmatter) {
      return { message: 'Cannot create hint: frontmatter is required.' };
    }
    if (!opts.element) {
      return { message: 'Cannot create hint: element description is required.' };
    }
    if (opts.frontmatter.length > 120) {
      return { message: 'Cannot create hint: frontmatter must be 120 characters or less.' };
    }
    if (opts.body && opts.body.length > 2000) {
      return { message: 'Cannot create hint: body must be 2000 characters or less.' };
    }
    if (/\be\d+\b/.test(opts.element)) {
      return {
        message:
          'Cannot create hint: element description must not contain ref identifiers (e.g., "e42"). Use real terms like "the gear icon".',
      };
    }
    if (/\b(it|this|that)\b/i.test(opts.element) && opts.element.split(/\s+/).length <= 3) {
      return {
        message: 'Cannot create hint: element description must use specific terms, not pronouns.',
      };
    }

    const category = opts.category ?? 'element';

    const created = await this.hintProvider.createHint({
      propertyId: this.propertyId,
      urlNorm,
      fingerprintHash: fpResult.hash,
      fingerprint: fpResult.fingerprint,
      category,
      element: opts.element,
      frontmatter: opts.frontmatter,
      body: opts.body ?? null,
      sourceType: 'workflow_run',
      sourceId: null,
      sessionId: null,
    });

    this.clearHintCache(urlNorm);

    this.logger?.debug?.('[browser_hint] Hint created', {
      hintId: created.id,
      propertyId: this.propertyId,
      urlNorm,
      fingerprintHash: fpResult.hash,
      category,
      anchoredToParent: fpResult.anchoredToParent,
    });

    const anchorNote = fpResult.anchoredToParent
      ? ` (anchored to ${fpResult.anchor.role} "${fpResult.anchor.text ?? ''}")`
      : '';
    return {
      message: `Hint created on [${ref}]${anchorNote}: (${category}: ${opts.frontmatter})`,
      created: {
        id: created.id,
        frontmatter: opts.frontmatter,
        category,
        fingerprintHash: fpResult.hash,
        urlNorm,
      },
    };
  }

  async screenshot(opts?: {
    fullPage?: boolean;
    type?: 'png' | 'jpeg';
    element?: string;
    ref?: string;
    /** Clip to a viewport region (page screenshots only — ignored when `ref` is set). */
    clip?: { x: number; y: number; width: number; height: number };
    label?: string;
    returnImage?: boolean;
  }): Promise<ToolResult> {
    const ref = normalizeRef(opts?.ref);
    const img = await this.client.takeScreenshot({
      fullPage: opts?.fullPage,
      element: opts?.element,
      ref,
      clip: opts?.clip,
      type: opts?.type ?? 'jpeg',
      label: opts?.label,
      signal: this.currentSignal,
    });

    if (this.onScreenshot && img) {
      await this.onScreenshot(img, opts?.label);
    }

    return formatScreenshotResult({
      imageDataUrl: img,
      label: opts?.label,
      fullPage: opts?.fullPage,
      maxImageDimension: this.maxImageDimension,
      logger: this.logger,
    });
  }

  async evaluate(opts: { expression: string; label?: string; ref?: string }): Promise<ToolResult> {
    const ref = normalizeRef(opts.ref);
    const result = await this.client.evaluate(opts.expression, {
      label: opts.label,
      ref,
      signal: this.currentSignal,
    });
    return typeof result === 'string' ? result : JSON.stringify(result, null, 2);
  }

  /**
   * `browser_scan` — bounded, read-only DOM traversal that returns ONE precise,
   * verified action locator (or a ranked ambiguous set) for a text query. Never
   * throws; the formatted `text` always tells the model its next move. Returns
   * the structured `status`/`refUsed` alongside the text so the tool layer emits
   * telemetry from real fields, not by re-parsing the rendered copy.
   */
  async scan(
    opts: FastDriveScanInput,
    /**
     * Render the annotated context crop (Improvement C). The live agent tool sets
     * this true (it can display images); the CLI/daemon path leaves it false and
     * stays text-only. Even when true, an image is only produced where text is
     * weakest (see the visual gate below).
     */
    renderVisual = false
  ): Promise<{ text: string; status: ScanStatus; refUsed: boolean; media?: MCPMediaContent[] }> {
    if (!this.client.scan) {
      return {
        text: 'status: not_found\nguidance: browser_scan is not supported by this browser client — use browser_snapshot.',
        status: 'not_found',
        refUsed: false,
      };
    }
    const result = await this.client.scan(opts, { signal: this.currentSignal });
    const out = {
      text: formatScanResultText(result),
      status: result.status,
      refUsed: result.ref != null,
    };
    if (!renderVisual) return out;

    // Visual gate: show an annotated CONTEXT CROP only where text alone is weakest
    // — the `ambiguous` ranked set (outline + number each candidate), a
    // coordinate-probe (visually confirm what the pixel resolved to), or an
    // explicit `visualize`. NEVER on a text-query `ready` — that protects the
    // cost win of the common confident case.
    if (!shouldRenderScanVisual(opts, result.status)) return out;
    const media = await this.renderScanVisual(opts, result).catch((err) => {
      // Image is optional; a breadcrumb is not. Without this, a regression in the
      // screenshot/annotate/crop pipeline silently disables the visual gate with
      // nothing in the logs to diagnose it.
      this.logger?.debug?.('[browser_scan] context-crop render failed', {
        error: err instanceof Error ? err.message : String(err),
      });
      return undefined;
    });
    return media ? { ...out, media } : out;
  }

  /**
   * Build the annotated context crop for a scan result: screenshot → draw a
   * numbered/colored outline on each candidate (in viewport coords) → crop to the
   * inflated union so the image keeps PAGE CONTEXT around the target(s), not a
   * decontextualized box. Best-effort; returns undefined on any hiccup so the scan
   * never fails on rendering.
   */
  private async renderScanVisual(
    opts: FastDriveScanInput,
    result: FastDriveScanResult
  ): Promise<MCPMediaContent[] | undefined> {
    const CONTEXT_PADDING = 160; // generous margin → page context, not a tight box
    const boxes: BoundingBox[] = scanHighlightBoxes(opts, result);
    if (boxes.length === 0) return undefined;

    const shot = await this.client.takeScreenshot({ type: 'jpeg', signal: this.currentSignal });
    const m = shot?.match(/^data:image\/(?:png|jpeg);base64,(.+)$/);
    if (!m) return undefined;
    const raw = m[1];
    const vp = this.client.getViewportSize();

    const annotated = await annotateScreenshotIndexed(
      raw,
      boxes.map((box, i) => ({ index: i + 1, box })),
      { originalDimensions: vp }
    );
    const union = boxes.reduce<BoundingBox>(
      (u, b) => {
        const x1 = Math.min(u.x, b.x);
        const y1 = Math.min(u.y, b.y);
        const x2 = Math.max(u.x + u.width, b.x + b.width);
        const y2 = Math.max(u.y + u.height, b.y + b.height);
        return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
      },
      { ...boxes[0] }
    );
    const cropped = await cropImage(annotated, union, vp, CONTEXT_PADDING);
    // Bound the payload: a `visualize:true` on an ambiguous set spanning most of
    // the page can produce a near-full-viewport crop, so cap its dimensions the
    // same way the plain screenshot path does before it lands in the tool result.
    const bounded = await downsampleImage(cropped, this.maxImageDimension);
    return [{ data: bounded, mimeType: 'image/jpeg' }];
  }

  /**
   * Execute driver-side Playwright code with access to the raw `page` object.
   * Unlike `evaluate` (in-page JS), this runs in Node and dispatches real input
   * events — so framework-managed inputs (React/Vue) update correctly. Mirrors
   * the in-process `browser_run_code` tool; intentionally does NOT auto-snapshot
   * (callers perceive on demand).
   */
  async runCode(opts: {
    code: string;
    observe?: 'immediate' | 'settle';
    /** Named in-scope bindings injected into the code (e.g. vars/macro/attach). */
    bindings?: Record<string, unknown>;
    /** Structural success signal (false when the code threw or timed out). */
    onOutcome?: (signals: RunCodeExecutionSignals) => void;
  }): Promise<ToolResult> {
    // Fast-drive observation. Unset → plain code run (backward-compatible).
    // 'immediate' → cheap "is something happening" signals, NO snapshot.
    // 'settle' → wait for the page to settle, return a compact ref-less diff.
    const sig = this.currentSignal;
    const immediate = opts.observe === 'immediate';
    const settle = opts.observe === 'settle';
    const observing = immediate || settle;

    // Cheap before-state, captured for BOTH observe modes so the result can
    // ground the agent: the URL it acted on (+ navigation), a modal or new tab
    // it opened, console errors it triggered. Each is an in-memory read or a
    // single page.evaluate — negligible next to settle's stabilize — and the
    // whole block is skipped on the no-observe path.
    const before = observing
      ? await captureRunCodeBeforeObservation({ client: this.client, signal: sig, settle })
      : undefined;

    // Stamp the console-error window right before the block runs, so the
    // before-reads above can't land inside it (tighter attribution).
    const startedAtMs = observing ? Date.now() : 0;
    const result = await this.client.runCode(opts.code, {
      signal: sig,
      bindings: opts.bindings,
      onOutcome: opts.onOutcome,
    });
    const resultText = formatRunCodeResult(result);

    if (!opts.observe) return resultText;

    if (immediate) {
      // Post-action signals are read AFTER the block (no settle wait).
      const post = await collectRunCodePostActionSignals({
        client: this.client,
        signal: sig,
        startedAtMs,
        beforeSignals: before?.beforeSignals,
        beforeReqCount: before?.beforeReqCount ?? 0,
        beforePageUrls: before?.beforePageUrls ?? [],
      });
      const pending = this.client.getPendingNetworkInfo?.() ?? { pendingCount: 0, sampleUrls: [] };
      return formatImmediateRunCodeObservation({
        resultText,
        beforeUrl: before?.beforeUrl,
        beforeSignals: before?.beforeSignals,
        post,
        pendingNetworkCount: pending.pendingCount,
      });
    }

    // settle — `captureReflessYaml` stabilizes (dom-only) internally and honors
    // the signal, so we do NOT also call `waitForNetworkIdle` here (that would
    // double-settle and ignore the abort signal). Read the post-action signals
    // AFTER the capture so a modal/tab/error that appears during settle counts.
    // `.catch(() => '')` — a block that closed/navigated the page makes the
    // capture throw (getPage → restore); fall into the empty-capture branch
    // below rather than rejecting and discarding the code's own result.
    const afterYaml =
      (await this.client.captureReflessYaml?.({ signal: sig }).catch(() => '')) ?? '';
    const post = await collectRunCodePostActionSignals({
      client: this.client,
      signal: sig,
      startedAtMs,
      beforeSignals: before?.beforeSignals,
      beforeReqCount: before?.beforeReqCount ?? 0,
      beforePageUrls: before?.beforePageUrls ?? [],
    });
    // Navigation is conveyed by the diff (and the no-baseline navNote) below, so
    // settle adds no standalone URL line — only the non-diff extras.
    const settled = formatSettleRunCodeObservation({
      resultText,
      beforeYaml: before?.beforeYaml ?? '',
      afterYaml,
      beforeUrl: before?.beforeUrl,
      post,
    });
    if (settled.captureEmpty) {
      // Capture failed (aborted / jammed renderer / empty). Don't diff against
      // nothing — surface it so the agent re-perceives explicitly.
      this.logger?.warn?.('[runCode] settle capture returned empty', {
        afterUrl: settled.afterUrl,
      });
    }
    return settled.text;
  }

  async consoleMessages(opts?: {
    onlyErrors?: boolean;
    sinceMs?: number;
    includePreStep?: boolean;
  }): Promise<ToolResult> {
    return await this.client.consoleMessages({
      onlyErrors: opts?.onlyErrors,
      sinceMs: opts?.sinceMs,
      includePreStep: opts?.includePreStep,
      signal: this.currentSignal,
    });
  }

  async networkRequests(): Promise<ToolResult> {
    return await this.client.networkRequests({ signal: this.currentSignal });
  }

  // ==================== Interaction ====================

  async click(opts: {
    ref?: string;
    x?: number;
    y?: number;
    element: string;
    doubleClick?: boolean;
    button?: string;
    modifiers?: string[];
  }): Promise<ToolResult> {
    // Drop defaulted (0, 0) coords — never an intentional click target. AI SDK 6
    // models occasionally emit `x: 0, y: 0` alongside a real ref; without this,
    // the coordinate path wins and every click lands at the page origin.
    const ref = normalizeRef(opts.ref);
    let x = opts.x;
    let y = opts.y;
    if (x === 0 && y === 0) {
      if (!ref) {
        return (
          'Error: Ref-less coordinate click at (0, 0) is invalid. ' +
          'The coordinates were likely defaulted instead of intentionally chosen. ' +
          'Call browser_snapshot and click by ref, or probe real coordinates before retrying.'
        );
      }
      x = undefined;
      y = undefined;
    }

    if (!ref && (x === undefined || y === undefined)) {
      return 'Error: Provide either ref or both x and y coordinates';
    }

    // Prefer ref when both are supplied — coords alongside ref are almost always
    // a tool-call default leak.
    const useCoordinates = !ref && x !== undefined && y !== undefined;

    const clickVerb = opts.doubleClick ? 'Double-clicked' : 'Clicked';
    const actionDesc = useCoordinates
      ? `${clickVerb} "${opts.element}" at (${x}, ${y})`
      : `${clickVerb} "${opts.element}" via ref ${ref}`;

    // Capture for replayLastAction using the normalized ref so blank/defaulted
    // tool args do not replay into Playwright.
    this.lastReplayableAction = {
      description: actionDesc,
      thunk: () => this.click({ ...opts, ref }),
    };

    let clickedElement: ClickedElementInfo | undefined;
    let fallbackUsed = false;

    let diffText: string;
    try {
      ({ diffText } = await this.withSemanticDiff(actionDesc, async () => {
        if (useCoordinates) {
          const coordResult = await this.client.clickAtCoordinates(x!, y!, opts.element, {
            doubleClick: opts.doubleClick,
            modifiers: opts.modifiers,
            signal: this.currentSignal,
          });
          clickedElement = coordResult.clickedElement;
        } else {
          const refResult = await this.client.click(ref!, opts.element, {
            doubleClick: opts.doubleClick,
            button: opts.button,
            modifiers: opts.modifiers,
            signal: this.currentSignal,
          });
          clickedElement = refResult.clickedElement;
          fallbackUsed = refResult.fallbackUsed;
        }
      }));
    } catch (error) {
      if (!useCoordinates && ref && isStaleAriaRefActionTimeout(error, 'click')) {
        return formatStaleRefActionRecovery('browser_click', ref, opts.element);
      }
      throw error;
    }

    let result = diffText;
    if (this.clickValidation) {
      const clickedBlock = formatClickedElement(clickedElement, fallbackUsed);
      if (clickedBlock) {
        result = diffText + '\n\n' + clickedBlock;
      }
    }

    // Native dialog message is surfaced inside withSemanticDiff so it lands
    // in diffText for ALL action paths (click/type/key/scroll/...), not just
    // click. No additional consume needed here.

    return withRecoveryNoticeMCP(this.client, result, this.logger);
  }

  async type(opts: {
    ref: string;
    text: string;
    element: string;
    submit?: boolean;
    delay?: number;
  }): Promise<ToolResult> {
    const ref = normalizeRef(opts.ref);
    if (!ref) {
      return formatEmptyRefRecovery('browser_type');
    }

    const submitNote = opts.submit ? ' and submitted' : '';
    const actionDesc = `Typed "${opts.text}" into "${opts.element}"${submitNote}`;

    // Capture for replayLastAction (only the submit path can fire a dialog,
    // but storing both keeps replay semantics consistent and harmless).
    this.lastReplayableAction = {
      description: actionDesc,
      thunk: () => this.type({ ...opts, ref }),
    };

    try {
      if (opts.submit) {
        const { diffText } = await this.withSemanticDiff(actionDesc, async () => {
          await this.client.type(ref, opts.text, opts.element, opts.submit, {
            delay: opts.delay,
            signal: this.currentSignal,
          });
        });
        return withRecoveryNoticeMCP(this.client, diffText, this.logger);
      }

      await this.client.type(ref, opts.text, opts.element, opts.submit, {
        delay: opts.delay,
        signal: this.currentSignal,
      });
      return withRecoveryNotice(this.client, actionDesc, this.logger);
    } catch (error) {
      if (isFillTargetNotEditableError(error)) {
        return formatFillTargetNotEditableRecovery(error, 'browser_type');
      }
      throw error;
    }
  }

  async hover(opts: { ref: string; element: string }): Promise<ToolResult> {
    const ref = normalizeRef(opts.ref);
    if (!ref) {
      return formatEmptyRefRecovery('browser_hover');
    }

    const { diffText } = await this.withSemanticDiff(
      `Hovered over "${opts.element}" via ref ${ref}`,
      async () => {
        await this.client.hover(ref, opts.element, { signal: this.currentSignal });
      }
    );
    return diffText;
  }

  async drag(opts: {
    startRef?: string;
    endRef?: string;
    startElement: string;
    endElement: string;
    startX?: number;
    startY?: number;
    endX?: number;
    endY?: number;
  }): Promise<ToolResult> {
    const startRef = normalizeRef(opts.startRef);
    const endRef = normalizeRef(opts.endRef);
    const useCoordinates =
      opts.startX !== undefined &&
      opts.startY !== undefined &&
      opts.endX !== undefined &&
      opts.endY !== undefined;
    const useRefs = startRef !== undefined && endRef !== undefined;

    if (!useCoordinates && !useRefs) {
      return 'Error: Provide either startRef + endRef, or all four coordinates (startX, startY, endX, endY)';
    }
    if (
      useCoordinates &&
      opts.startX === 0 &&
      opts.startY === 0 &&
      opts.endX === 0 &&
      opts.endY === 0
    ) {
      return (
        'Error: Coordinate drag at (0, 0) -> (0, 0) is invalid. ' +
        'The coordinates were likely defaulted instead of intentionally chosen. ' +
        'Use refs for drag-and-drop, or supply real coordinates before retrying.'
      );
    }

    const actionDesc = useCoordinates
      ? `Dragged from (${opts.startX}, ${opts.startY}) to (${opts.endX}, ${opts.endY})`
      : `Dragged "${opts.startElement}" to "${opts.endElement}"`;

    const { diffText } = await this.withSemanticDiff(actionDesc, async () => {
      if (useCoordinates) {
        await this.client.dragCoordinates(
          opts.startX!,
          opts.startY!,
          opts.endX!,
          opts.endY!,
          opts.startElement,
          { signal: this.currentSignal }
        );
      } else {
        await this.client.drag(startRef!, opts.startElement, endRef!, opts.endElement, {
          signal: this.currentSignal,
        });
      }
    });
    return diffText;
  }

  async pressKey(key: string): Promise<ToolResult> {
    const normalizedKey = normalizePressKey(key);

    if (normalizedKey.length === 0) {
      return formatEmptyPressKeyRecovery();
    }

    // Capture for replayLastAction. Enter on a form can submit and fire
    // confirm(); other keys are harmless to replay.
    this.lastReplayableAction = {
      description: `Pressed ${normalizedKey}`,
      thunk: () => this.pressKey(normalizedKey),
    };

    if (ACTION_KEYS.has(normalizedKey)) {
      const { diffText } = await this.withSemanticDiff(`Pressed ${normalizedKey}`, async () => {
        await this.client.pressKey(normalizedKey, { signal: this.currentSignal });
      });
      return withRecoveryNoticeMCP(this.client, diffText, this.logger);
    }

    await this.client.pressKey(normalizedKey, { signal: this.currentSignal });
    return withRecoveryNotice(this.client, `Pressed ${normalizedKey}`, this.logger);
  }

  async fillForm(
    fields: Array<{ ref: string; element?: string; value: string }>
  ): Promise<ToolResult> {
    const normalizedFields = fields.map((field) => ({
      ...field,
      ref: normalizeRef(field.ref) ?? '',
    }));
    if (normalizedFields.some((field) => field.ref.length === 0)) {
      return formatEmptyRefRecovery('browser_fill_form');
    }

    try {
      const { diffText } = await this.withSemanticDiff(
        `Filled ${fields.length} form field(s)`,
        async () => {
          await this.client.fillForm(normalizedFields, { signal: this.currentSignal });
        }
      );
      return diffText;
    } catch (error) {
      if (isFillTargetNotEditableError(error)) {
        return formatFillTargetNotEditableRecovery(error);
      }
      if (isStaleAriaRefActionTimeout(error, 'fill')) {
        const staleRef = extractWaitingAriaRef(error);
        const field =
          normalizedFields.find((candidate) => candidate.ref === staleRef) ?? normalizedFields[0];
        return formatStaleRefActionRecovery(
          'browser_fill_form',
          staleRef ?? field?.ref ?? 'unknown',
          field?.element
        );
      }
      throw error;
    }
  }

  async selectOption(opts: { ref: string; value: string; element: string }): Promise<ToolResult> {
    const ref = normalizeRef(opts.ref);
    if (!ref) {
      return formatEmptyRefRecovery('browser_select_option');
    }

    try {
      const { diffText } = await this.withSemanticDiff(
        `Selected "${opts.value}" in "${opts.element}"`,
        async () => {
          await this.client.selectOption(ref, opts.value, opts.element, {
            signal: this.currentSignal,
          });
        }
      );
      return diffText;
    } catch (error) {
      if (isSelectOptionNonSelectError(error)) {
        return formatSelectOptionNonSelectRecovery(ref, opts.value, opts.element);
      }
      throw error;
    }
  }

  async fileUpload(
    paths: string[],
    ref?: string,
    remoteFetchFiles?: Array<{ url: string; filename: string } | undefined>
  ): Promise<ToolResult> {
    const normalizedRef = normalizeRef(ref);
    const { diffText } = await this.withSemanticDiff(
      `Uploaded ${paths.length} file(s)`,
      async () => {
        await this.client.fileUpload(paths, {
          ref: normalizedRef,
          signal: this.currentSignal,
          remoteFetchFiles,
        });
      }
    );
    return diffText;
  }

  // ==================== Scroll ====================

  async scroll(opts: {
    direction: 'up' | 'down' | 'left' | 'right';
    amount?: number;
    withinRef?: string;
    toRef?: string;
    x?: number;
    y?: number;
  }): Promise<ToolResult> {
    let result: string | ScrollResult;
    let label: string;
    const withinRef = normalizeRef(opts.withinRef);
    const toRef = normalizeRef(opts.toRef);

    // Mode 1: Scroll to element
    if (toRef) {
      result = await this.client.scroll(opts.direction, opts.amount, withinRef, toRef, {
        signal: this.currentSignal,
      });
      label = `toRef=${toRef}`;
    } else if (opts.x !== undefined && opts.y !== undefined) {
      if (opts.x === 0 && opts.y === 0) {
        return (
          'Error: Coordinate scroll at (0, 0) is invalid. ' +
          'The coordinates were likely defaulted instead of intentionally chosen. ' +
          'Use direction-based scroll, scroll to a ref, or provide real coordinates before retrying.'
        );
      }
      // Mode 2: Coordinate-based scroll
      result = await this.client.scrollAtCoordinates(opts.x, opts.y, opts.direction, opts.amount, {
        signal: this.currentSignal,
      });
      label = `direction=${opts.direction} at (${opts.x}, ${opts.y})${opts.amount ? `, amount=${opts.amount}` : ''}`;
    } else {
      // Mode 3: Direction-based scroll
      result = await this.client.scroll(opts.direction, opts.amount, withinRef, undefined, {
        signal: this.currentSignal,
      });
      label = `direction=${opts.direction}${opts.amount ? `, amount=${opts.amount}` : ''}${withinRef ? `, withinRef=${withinRef}` : ''}`;
    }

    const scrollInfo: ScrollInfo | undefined =
      typeof result === 'object' && result !== null ? result.scrollInfo : undefined;

    // No movement: return a compact hint with no aria tree / screenshot.
    // The DOM hasn't changed — echoing the prior snapshot only confuses the agent.
    if (scrollInfo && !scrollInfo.moved) {
      return { text: formatNoMovementHint(label, scrollInfo) };
    }

    // Movement: prepend a position header so the agent knows where it is.
    const header = scrollInfo
      ? `Scroll: ${label} → moved ${formatPosition(scrollInfo.before)} → ${formatPosition(scrollInfo.after)}${formatGeometryInline(scrollInfo)}`
      : `Scrolled ${label}`;

    if (isMCPContentWithMedia(result)) {
      const media = getMCPContentMedia(result);
      return {
        text: `${header}\n${result.text}`.trim(),
        media,
        images: result.images ?? media,
      };
    }
    return header;
  }

  // ==================== Dialogs ====================

  /**
   * Pre-arm the session's single dialog handler for the next native dialog —
   * the race-free way for `browser_run_code` to handle a `confirm`/`alert`/
   * `prompt` it is about to trigger. The agent calls this (via the `dialog`
   * run_code binding) *before* the triggering action, in the same block:
   * `dialog("accept"); await page.getByRole(...).click();`. Unlike
   * `handleDialog`, this is a pure synchronous arm — it does NOT shift any
   * queued dialog or replay a prior action. Reuses the exact mechanism that
   * `expectedDialogs` playbook replay uses (`client.setNextDialogAction`); no
   * new dialog machinery. No-ops safely if the client doesn't implement it.
   */
  armDialog(action: 'accept' | 'dismiss', promptText?: string): void {
    this.client.setNextDialogAction?.(action, promptText);
  }

  async handleDialog(opts: {
    action: 'accept' | 'dismiss';
    promptText?: string;
    /**
     * After pre-arming the dialog handler, re-issue the most recent
     * click/type/pressKey so the agent doesn't have to do it as a separate
     * tool call. Eliminates the "click twice, hope nothing changed in
     * between" footgun. If no replayable action is available, the call
     * still pre-arms the handler and reports that fact.
     */
    replayLastAction?: boolean;
  }): Promise<ToolResult> {
    const result = await this.client.handleDialog(opts.action, opts.promptText, {
      signal: this.currentSignal,
    });
    const armed = typeof result === 'string' ? result : `Dialog ${opts.action}ed`;

    if (!opts.replayLastAction) return armed;

    const replay = this.lastReplayableAction;
    if (!replay) {
      return (
        armed +
        '\n\n[REPLAY SKIPPED] replayLastAction was true, but no recent ' +
        'click/type/pressKey is recorded. The dialog handler is pre-armed; ' +
        'issue your action now.'
      );
    }

    const replayResult = await replay.thunk();
    // Consume AFTER the replay — the thunk re-invokes the public action
    // method, which itself re-records lastReplayableAction. Clearing here
    // makes replay single-shot: a second handle_dialog(replay:true) without
    // a fresh agent-issued action will not re-fire (would otherwise risk
    // runaway loops, e.g. accidental double-revoke).
    this.lastReplayableAction = null;
    this.logger?.info?.('[BrowserToolExecutor] dialog replay re-issued', {
      replayDescription: replay.description,
      dialogAction: opts.action,
    });
    const header = `[REPLAY] Re-issued: ${replay.description}`;
    const prefix = `${armed}\n\n${header}\n\n`;
    if (typeof replayResult === 'string') {
      return prefix + replayResult;
    }
    return { text: prefix + replayResult.text, images: replayResult.images };
  }

  async dismissOverlay(opts?: {
    preferredStrategy?: 'escape' | 'close_button' | 'backdrop' | 'auto';
  }): Promise<ToolResult> {
    const preferredStrategy = opts?.preferredStrategy ?? 'auto';
    const strategies =
      preferredStrategy === 'auto'
        ? (['escape', 'close_button', 'backdrop'] as const)
        : ([preferredStrategy] as const);

    const beforeUrl = await this.client.getCurrentUrl();
    const beforeSnapshot = await this.client.snapshot({ signal: this.currentSignal, mode: 'tree' });
    const beforeYaml = extractSnapshotYaml(beforeSnapshot);
    const beforeModal = detectModalFromYaml(beforeYaml);
    const beforeState = captureSnapshotState(beforeUrl, beforeYaml);

    if (!beforeModal) {
      return 'No modal/overlay detected - page appears clear. Ready for interaction.';
    }

    for (const strategy of strategies) {
      try {
        switch (strategy) {
          case 'escape':
            await this.client.pressKey('Escape', { signal: this.currentSignal });
            break;
          case 'close_button': {
            const closeRef = findCloseButton(beforeYaml);
            if (closeRef) {
              await this.client.click(closeRef, 'close button', { signal: this.currentSignal });
            } else {
              continue;
            }
            break;
          }
          case 'backdrop':
            await this.client.clickAtCoordinates(10, 10, 'backdrop/outside modal', {
              signal: this.currentSignal,
            });
            break;
        }

        await this.client.waitFor({ timeSec: 0.5, signal: this.currentSignal });

        const afterUrl = await this.client.getCurrentUrl();
        const afterSnapshot = await this.client.snapshot({
          signal: this.currentSignal,
          mode: 'tree',
        });
        const afterYaml = extractSnapshotYaml(afterSnapshot);
        const afterModal = detectModalFromYaml(afterYaml);

        if (!afterModal) {
          const afterState = captureSnapshotState(afterUrl, afterYaml);
          const diff = compareSnapshots(beforeState, afterState);
          return formatSemanticDiff(
            diff,
            `Dismissed overlay using ${strategy}`,
            undefined,
            new Date().toISOString()
          );
        }
      } catch (err) {
        this.logger?.debug?.(`[BrowserTools] Dismiss strategy ${strategy} failed`, {
          error: err instanceof Error ? err.message : String(err),
        });
        continue;
      }
    }

    // All strategies failed
    const afterUrl = await this.client.getCurrentUrl();
    const afterSnapshot = await this.client.snapshot({ signal: this.currentSignal, mode: 'tree' });
    const afterYaml = extractSnapshotYaml(afterSnapshot);
    const afterState = captureSnapshotState(afterUrl, afterYaml);
    const diff = compareSnapshots(beforeState, afterState);

    return (
      `Could not dismiss overlay with strategies: ${strategies.join(', ')}. ` +
      `Modal may require specific interaction.\n\n` +
      formatSemanticDiff(
        diff,
        'Dismiss overlay attempted (failed)',
        undefined,
        new Date().toISOString()
      )
    );
  }

  // ==================== Waiting ====================

  async waitFor(opts: {
    timeSec?: number;
    text?: string;
    textGone?: string;
    selector?: string;
    state?: string;
    timeout?: number;
  }): Promise<ToolResult> {
    let selectorState: BrowserWaitForSelectorState = 'visible';
    if (opts.selector && opts.state !== undefined) {
      if (!isBrowserWaitForSelectorState(opts.state)) {
        return formatInvalidWaitForSelectorState(opts.state);
      }
      selectorState = opts.state;
    }

    const result = await this.client.waitFor({
      timeSec: opts.timeSec,
      text: opts.text,
      textGone: opts.textGone,
      selector: opts.selector,
      state: selectorState,
      timeout: opts.timeout,
      signal: this.currentSignal,
    });
    return typeof result === 'string' ? result : 'Wait completed';
  }

  // ==================== Browser Management ====================

  async close(): Promise<ToolResult> {
    const result = await this.client.close({ signal: this.currentSignal });
    return typeof result === 'string' ? result : 'Page closed';
  }

  async resize(width: number, height: number): Promise<ToolResult> {
    const result = await this.client.resize(width, height, { signal: this.currentSignal });
    return typeof result === 'string' ? result : `Resized to ${width}x${height}`;
  }

  async tabs(opts: {
    action: 'list' | 'new' | 'close' | 'select';
    index?: number;
    handle?: number;
    match?: string;
  }): Promise<ToolResult> {
    // Post-wedge footgun guard: opening or closing a tab after a [PAGE WEDGED]
    // hint reassigns `this.page` to a fresh `about:blank` (per playwright-client
    // tabs/new and tabs/close branches). Every subsequent page-level tool then
    // returns instantly with empty results, which the agent reads as
    // confirmation that "the session is dead" — and the segment idles to the
    // 30-min stall guard. The wedge is on the *current* page; opening or
    // closing tabs cannot help. Force the agent back to direct recovery.
    if (
      this.consecutiveSnapshotTimeouts > 0 &&
      (opts.action === 'new' || opts.action === 'close')
    ) {
      return (
        `[REJECTED] You called browser_tabs(action='${opts.action}') after a PAGE WEDGED hint. ` +
        `This switches the active page to about:blank and breaks the session further. ` +
        `The wedge is on the current page — opening or closing a tab does not help. ` +
        `Either call browser_handle_dialog action='accept' (a native dialog may still be open), ` +
        `browser_navigate to the current URL, or fail this step.`
      );
    }
    const selector =
      opts.handle !== undefined || opts.match !== undefined
        ? { index: opts.index, handle: opts.handle, match: opts.match }
        : opts.index;
    const result = await this.client.tabs(opts.action, selector, { signal: this.currentSignal });
    return typeof result === 'string' ? result : JSON.stringify(result, null, 2);
  }

  // ==================== Downloads ====================

  async listDownloads(): Promise<ToolResult> {
    return await this.client.listDownloads({ signal: this.currentSignal });
  }

  async readDownload(downloadId?: number): Promise<ToolResult> {
    let result: {
      filename: string;
      sizeBytes: number;
      mimeType: string;
      textContent: string | null;
    };
    try {
      result = await this.client.readDownload(downloadId, { signal: this.currentSignal });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message === 'No downloads captured during this session.') {
        return (
          `${message} Use browser_list_downloads after triggering a download, or ` +
          'try browser_pdf_read if the file opened inline instead of downloading.'
        );
      }
      if (
        downloadId === 0 &&
        message === 'Download #0 not found. Use browser_list_downloads to see available downloads.'
      ) {
        return (
          'Download #0 was not found. Download ids start at 1; call browser_list_downloads ' +
          'and use the listed id, or omit downloadId to read the most recent download.'
        );
      }
      if (isMissingDownloadTempFileError(message)) {
        const label =
          downloadId !== null && downloadId !== undefined
            ? `Download #${downloadId}`
            : 'The most recent download';
        return (
          `${label} was captured, but its temporary file is no longer available. ` +
          'Trigger the download again, then call browser_list_downloads and browser_read_download with the new id.'
        );
      }
      throw err;
    }
    const lines: string[] = [
      `Filename: ${result.filename}`,
      `Size: ${result.sizeBytes} bytes`,
      `Type: ${result.mimeType}`,
    ];
    if (result.textContent !== null && result.textContent !== undefined) {
      lines.push('', '--- Content ---', result.textContent);
    } else {
      lines.push('', '[Binary file — content cannot be displayed as text]');
    }
    return lines.join('\n');
  }

  async fetchPdfText(url: string): Promise<ToolResult> {
    const protocol = getAbsoluteUrlProtocol(url);
    if (protocol && protocol !== 'http:' && protocol !== 'https:') {
      return formatUnsupportedPdfReadUrlRecovery(url, protocol);
    }

    const result = await this.client.fetchPdfText(url, { signal: this.currentSignal });
    const lines: string[] = [
      `Pages: ${result.totalPages}`,
      `Size: ${result.sizeBytes} bytes`,
      '',
      '--- Content ---',
      result.text,
    ];
    return lines.join('\n');
  }

  async fetchPdfImage(url: string): Promise<ToolResult> {
    const protocol = getAbsoluteUrlProtocol(url);
    if (protocol && protocol !== 'http:' && protocol !== 'https:') {
      return formatUnsupportedPdfReadUrlRecovery(url, protocol);
    }

    const result = await this.client.fetchPdfImage(url, { signal: this.currentSignal });
    const media = [{ data: result.base64, mimeType: result.mediaType }];
    return {
      text: `PDF fetched for visual reading (${result.sizeBytes} bytes). The document is attached below — read it directly, including any laid-out form boxes.`,
      media,
      images: media,
    };
  }

  /**
   * Fetch a PDF and save it into the Python sandbox workspace so `python_execute`
   * can parse it with a PDF library (pdfplumber / pypdf). Reuses the authenticated,
   * 10MB-capped fetch (`fetchPdfImage`) — the 10MB cap aligns with the sandbox's
   * per-file limit. The staged file is readable in the sandbox at `/workspace/<name>`.
   */
  async fetchPdfToWorkspace(url: string): Promise<ToolResult> {
    const protocol = getAbsoluteUrlProtocol(url);
    if (protocol && protocol !== 'http:' && protocol !== 'https:') {
      return formatUnsupportedPdfReadUrlRecovery(url, protocol);
    }

    const { base64, sizeBytes } = await this.client.fetchPdfImage(url, {
      signal: this.currentSignal,
    });

    const filename = pdfWorkspaceFilename(url);
    const workspaceDir = this.getWorkspaceDir();
    await fs.mkdir(workspaceDir, { recursive: true });
    await fs.writeFile(path.join(workspaceDir, filename), Buffer.from(base64, 'base64'));

    return [
      `PDF saved to /workspace/${filename} (${sizeBytes} bytes).`,
      'Parse it in python_execute — pdfplumber for layout/coordinates, pypdf for form fields:',
      '```python',
      'import pdfplumber',
      `pdf = pdfplumber.open("${filename}")`,
      'page = pdf.pages[0]',
      'words = page.extract_words()   # each: text, x0/x1/top/bottom (top-origin)',
      'tables = page.extract_tables()',
      '',
      'import pypdf',
      `fields = pypdf.PdfReader("${filename}").get_fields() or {}   # AcroForm {name: {"/V": value}}`,
      '```',
    ].join('\n');
  }

  // ==================== Internal ====================

  /**
   * Wraps an action with before/after snapshot capture and semantic diff generation.
   */
  private async withSemanticDiff<T = void>(
    actionDescription: string,
    action: () => Promise<T>
  ): Promise<{
    diffText: string;
    actionResult: T;
  }> {
    // Per-action LiveSnapshot span attributes (emitted once at the end as a
    // `browser.live_snapshot` child span). Populated only when the feature is on.
    const liveAttrs: Record<string, string | number | boolean> = {};

    // Capture BEFORE state. With live-snapshot on, reuse the warm snapshot as
    // the diff baseline when the DOM hasn't drifted since it was minted —
    // turning the pre-action capture into a cheap revision check instead of a
    // full substrate rebuild. Falls back to a fresh capture when there's no
    // valid warm snapshot (first action, or the page drifted during think-time,
    // which preserves diff correctness).
    const beforeUrl = await this.client.getCurrentUrl();
    let beforeYaml: string | null = null;
    if (this.liveSnapshotEnabled) {
      const t0 = Date.now();
      beforeYaml = (await this.client.getReusableSnapshotYaml?.()) ?? null;
      const dt = Date.now() - t0;
      const reuseHit = beforeYaml !== null;
      this.client.recordLiveSnapshotEvent?.({ kind: 'reuse', hit: reuseHit });
      this.client.recordLiveSnapshotEvent?.({ kind: 'before-capture', ms: dt });
      liveAttrs['snapshot.reuse_hit'] = reuseHit;
      liveAttrs['snapshot.before_capture_ms'] = dt;
    }
    if (beforeYaml === null) {
      const t1 = Date.now();
      const beforeResult = await this.client.snapshot({ signal: this.currentSignal, mode: 'tree' });
      if (this.liveSnapshotEnabled) {
        const dt = Date.now() - t1;
        // Count the fresh fallback toward before-capture too, so "capture latency
        // before vs after the feature" stays apples-to-apples.
        this.client.recordLiveSnapshotEvent?.({ kind: 'before-capture', ms: dt });
        liveAttrs['snapshot.before_capture_ms'] =
          (Number(liveAttrs['snapshot.before_capture_ms']) || 0) + dt;
      }
      beforeYaml = extractSnapshotYaml(beforeResult);
    }
    const beforeState = captureSnapshotState(beforeUrl, beforeYaml);

    // Perform action
    const actionResult = await action();

    // Capture AFTER state. Bounded by POST_ACTION_SNAPSHOT — if the page is
    // wedged (e.g., page.evaluate blocked by a native dialog) we synthesize a
    // diff so the action result still reaches the agent and the run doesn't
    // burn the stall-watchdog budget here.
    let afterUrl = beforeUrl;
    let afterResult: string | MCPContentResult = '';
    let afterYaml = '';
    let afterState = beforeState;
    let snapshotTimedOut = false;

    // Per-snapshot abort: if the post-action snapshot budget elapses, we abort
    // the controller to short-circuit `client.snapshot()`'s internal Playwright
    // ops AND prevent it from publishing late writes to `lastSnapshotYaml` /
    // `_lastCapturedToasts`. Combined with `currentSignal` so the per-tool
    // timeout still propagates.
    const snapshotAbort = new AbortController();
    const snapshotSignal = combineSignals(this.currentSignal, snapshotAbort.signal);

    const captureAfter = async () => {
      // Wait for network to settle after action before capturing snapshot.
      // Returns immediately if already idle (zero overhead for simple actions).
      await this.client.waitForNetworkIdle?.();
      if (snapshotAbort.signal.aborted) return;

      afterUrl = await this.client.getCurrentUrl();
      const afterT0 = Date.now();
      afterResult = await this.client.snapshot({
        signal: snapshotSignal,
        mode: 'tree',
        maxImageDimension: this.maxImageDimension,
      });
      if (this.liveSnapshotEnabled) {
        const dt = Date.now() - afterT0;
        this.client.recordLiveSnapshotEvent?.({ kind: 'after-capture', ms: dt });
        liveAttrs['snapshot.after_capture_ms'] = dt;
      }
      afterYaml = extractSnapshotYaml(afterResult);
      afterState = captureSnapshotState(afterUrl, afterYaml);

      // Retry empty snapshots caused by SPA navigation rendering delays
      if (beforeState.elements.length > 0 && afterState.elements.length === 0) {
        this.logger?.warn?.('[withSemanticDiff] Empty snapshot after action, retrying...', {
          beforeElements: beforeState.elements.length,
          beforeUrl,
          afterUrl,
        });

        const MAX_RETRIES = 3;
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          if (snapshotAbort.signal.aborted) break;
          // On first retry with URL change, do a full navigation idle wait
          if (attempt === 1 && afterUrl !== beforeUrl) {
            await this.client.waitForNetworkIdle?.(TIMEOUTS.STABILIZE_NAV);
          } else {
            await new Promise((r) => setTimeout(r, attempt * 500));
          }
          if (snapshotAbort.signal.aborted) break;

          afterUrl = await this.client.getCurrentUrl();
          afterResult = await this.client.snapshot({
            signal: snapshotSignal,
            mode: 'tree',
            maxImageDimension: this.maxImageDimension,
          });
          afterYaml = extractSnapshotYaml(afterResult);
          afterState = captureSnapshotState(afterUrl, afterYaml);

          if (afterState.elements.length > 0) {
            this.logger?.info?.('[withSemanticDiff] Snapshot populated on retry', {
              attempt,
              elements: afterState.elements.length,
            });
            break;
          }
        }

        if (afterState.elements.length === 0) {
          this.logger?.warn?.('[withSemanticDiff] Snapshot still empty after retries, proceeding');
        }
      }
    };

    const SNAPSHOT_TIMEOUT_SYMBOL = Symbol('post-action-snapshot-timeout');
    let snapshotTimer: ReturnType<typeof setTimeout> | undefined;
    try {
      const capturePromise = captureAfter();
      // Prevent unhandled rejection if the orphaned capture eventually fails.
      // Safe now that `snapshotAbort` gates the snapshot's internal `this.*`
      // writes — a late settling capture can't corrupt PlaywrightClient state.
      capturePromise.catch(() => {});
      await Promise.race([
        capturePromise,
        new Promise<never>((_, reject) => {
          snapshotTimer = setTimeout(() => {
            snapshotTimedOut = true;
            snapshotAbort.abort();
            reject(SNAPSHOT_TIMEOUT_SYMBOL);
          }, this.postActionSnapshotTimeoutMs);
        }),
      ]);
    } catch (err) {
      if (err === SNAPSHOT_TIMEOUT_SYMBOL) {
        this.logger?.warn?.('[withSemanticDiff] Post-action snapshot timed out', {
          actionDescription,
          beforeUrl,
          budgetMs: this.postActionSnapshotTimeoutMs,
        });
      } else {
        throw err;
      }
    } finally {
      if (snapshotTimer) clearTimeout(snapshotTimer);
    }

    // On snapshot timeout, skip the diff (before/after are equal by
    // construction) and return a synthetic warning alongside the action
    // result. The agent still sees that the action ran; the warning tells it
    // the follow-up observation failed so it should recover via a snapshot
    // or dialog handler before retrying.
    if (snapshotTimedOut) {
      this.consecutiveSnapshotTimeouts += 1;
      // A slow post-action snapshot is NOT evidence of a wedge — on a heavy page
      // (large DOM / Blazor) the a11y capture legitimately takes a while, and the
      // page is alive. We deliberately do NOT force-recover here: closing and
      // rebuilding the browser context dropped the authenticated session and
      // stormed Playwright wire-teardown errors (prod SEV1, June 2026). Instead we
      // degrade to a no-op diff + a re-observe hint and let the agent try again.
      // The consecutiveSnapshotTimeouts streak below only escalates the AGENT-
      // FACING guidance ([PAGE WEDGED] / [SESSION UNRECOVERABLE]); it never tears
      // the page down. Genuine wedges are caught by the page crash/close events
      // and the step-deadline backstop.
      // TODO(browser-recovery): reintroduce a *reliable* mid-run recovery for a
      // genuinely-wedged-but-open page — one that does not mistake slowness for
      // death (the 750ms probeLiveness gate did) and does not drop auth on rebuild.
      const now = new Date().toISOString();
      const baseDiff = formatSemanticDiff(
        compareSnapshots(beforeState, beforeState),
        actionDescription,
        undefined,
        now
      );
      const lastUrl = beforeUrl;
      let recoveryHint: string;
      if (this.consecutiveSnapshotTimeouts >= this.sessionUnrecoverableThreshold) {
        // Hard ceiling. The agent should stop iterating recovery primitives
        // and fail the step — this is the trapdoor that prod run 89d912d1
        // didn't have.
        this.logger?.info?.('[BrowserToolExecutor] session unrecoverable', {
          wedgeStreak: this.consecutiveSnapshotTimeouts,
          threshold: this.sessionUnrecoverableThreshold,
          lastUrl,
          actionDescription,
        });
        recoveryHint =
          `[SESSION UNRECOVERABLE] ${this.consecutiveSnapshotTimeouts} consecutive ` +
          `post-action snapshot timeouts. The browser session is not usable. ` +
          `Stop attempting recovery — fail this step, write a clear failure ` +
          `report (last known URL: ${lastUrl}), and let the orchestrator ` +
          `decide whether to restart the session.`;
      } else if (this.consecutiveSnapshotTimeouts >= this.pageWedgedThreshold) {
        this.logger?.info?.('[BrowserToolExecutor] page wedged', {
          wedgeStreak: this.consecutiveSnapshotTimeouts,
          threshold: this.pageWedgedThreshold,
          lastUrl,
          actionDescription,
        });
        recoveryHint =
          `[PAGE WEDGED] ${this.consecutiveSnapshotTimeouts} consecutive post-action ` +
          `snapshot timeouts. The page is not responding to observation. ` +
          `Most common cause: a native confirm/alert is still open and blocking ` +
          `the renderer thread. First try browser_handle_dialog action='accept' ` +
          `(or 'dismiss' if cancel is correct) — if a dialog is open, this clears it. ` +
          `Otherwise: browser_navigate to "${lastUrl}" to reset the page. ` +
          `Do NOT call browser_close OR browser_tabs(action='new'/'close') — both ` +
          `leave you on about:blank and the rejection is enforced post-wedge.`;
      } else {
        recoveryHint = `[WARN] Post-action snapshot timed out after ${Math.round(
          this.postActionSnapshotTimeoutMs / 1000
        )}s — the page was slow to respond (often a heavy DOM under load), not necessarily wedged. The action ran, but the resulting state could not be captured. The session was NOT recycled, so just re-observe: call browser_snapshot to re-attempt observation. If a native dialog is pending, browser_handle_dialog with action:"accept" and replayLastAction:true (one call clears the dialog and re-issues your last action).`;
      }
      let diffText = `${baseDiff}\n\n${recoveryHint}`;
      const dialogMsg = this.client.consumePendingDialogMessage?.();
      if (dialogMsg) diffText = diffText + '\n\n' + dialogMsg;
      return { diffText, actionResult };
    }
    // Successful capture — clear the timeout streak so the next wedge starts
    // fresh (a one-off slow snapshot during e.g. heavy SPA rendering doesn't
    // tip the next failure straight into [PAGE WEDGED]).
    this.consecutiveSnapshotTimeouts = 0;

    const diff = compareSnapshots(beforeState, afterState);
    const networkInfo = this.client.getPendingNetworkInfo?.() ?? undefined;
    const now = new Date().toISOString();
    let diffText = formatSemanticDiff(diff, actionDescription, networkInfo, now);
    // Co-locate any native dialog auto-dismissal with the action that
    // triggered it, instead of waiting for the next page tool to surface it.
    // Click also calls consumePendingDialogMessage (idempotent — queue is
    // shifted), but other action paths (type, key, scroll, navigate) only
    // see the message via this hook.
    const pendingDialogMsg = this.client.consumePendingDialogMessage?.();
    if (pendingDialogMsg) diffText = diffText + '\n\n' + pendingDialogMsg;

    // Emit the per-action LiveSnapshot attributes as a short child span under the
    // active ai.tool span, for per-action trace debugging in Sentry. The per-step
    // aggregate (metadata_json.liveSnapshot) is the queryable GA signal; this is
    // the drill-down. Best-effort — never affects the action result.
    if (
      this.liveSnapshotEnabled &&
      this.spanFactory?.setAttributes &&
      Object.keys(liveAttrs).length
    ) {
      const handle = this.spanFactory.startSpan('live_snapshot', actionDescription);
      this.spanFactory.setAttributes(handle, liveAttrs);
      this.spanFactory.endSpan(handle, 'ok');
    }

    // Warm-on-idle: refresh the warm snapshot in the background during the
    // agent's think-time, so the next action's before-capture is instant even
    // if the page keeps drifting after this action settled. Fire-and-forget;
    // no-op when live-snapshot is disabled.
    this.client.warmSnapshot?.();

    return { diffText, actionResult };
  }

  /**
   * Format a navigation result by replacing raw YAML with semantic snapshot.
   */
  private async formatNavigateResult(result: string): Promise<string> {
    const yaml = extractSnapshotYaml(result);
    if (!yaml) return result;

    // Write snapshot to workspace
    let snapshotFilePath: string | undefined;
    try {
      await fs.mkdir(this.workspaceDir, { recursive: true });
      const filename = `snapshot-${Date.now()}-${crypto.randomUUID().slice(0, 8)}.yaml`;
      snapshotFilePath = path.join(this.workspaceDir, filename);
      await fs.writeFile(snapshotFilePath, yaml, 'utf-8');
    } catch {
      // Non-critical
    }

    const semanticText = await formatSnapshotMaybeOffloaded(yaml, {
      snapshotFilePath,
      currentTime: new Date().toISOString(),
      capturedToasts: this.client.getCapturedToasts?.() ?? [],
      consoleErrors: this.client.getNewConsoleErrors?.() ?? [],
      failedRequests: this.client.getNewFailedRequests?.() ?? [],
    });

    const yamlBlockPattern = /- Page Snapshot:\n```yaml\n[\s\S]*?```/;
    if (yamlBlockPattern.test(result)) {
      return result.replace(yamlBlockPattern, `- Page Snapshot (semantic):\n${semanticText}`);
    }

    return result + '\n\n' + semanticText;
  }
}

// ==================== Scroll helpers ====================

function formatPosition(p: { x: number; y: number }): string {
  if (p.x === 0) return `y=${Math.round(p.y)}`;
  if (p.y === 0) return `x=${Math.round(p.x)}`;
  return `(x=${Math.round(p.x)}, y=${Math.round(p.y)})`;
}

function formatGeometryInline(info: ScrollInfo): string {
  const g = info.geometry;
  if (!g) return '';
  const vertical = info.direction === 'up' || info.direction === 'down';
  if (vertical && g.scrollHeight !== undefined && g.clientHeight !== undefined) {
    return ` (page height=${g.scrollHeight}, viewport=${g.clientHeight})`;
  }
  if (!vertical && g.scrollWidth !== undefined && g.clientWidth !== undefined) {
    return ` (page width=${g.scrollWidth}, viewport=${g.clientWidth})`;
  }
  return '';
}

function formatNoMovementHint(label: string, info: ScrollInfo): string {
  const vertical = info.direction === 'up' || info.direction === 'down';
  const axis = vertical ? 'y' : 'x';
  const currentPos = vertical ? info.after.y : info.after.x;
  const maxPos = vertical ? info.geometry?.maxScrollY : info.geometry?.maxScrollX;
  const clientDim = vertical ? info.geometry?.clientHeight : info.geometry?.clientWidth;
  const scrollDim = vertical ? info.geometry?.scrollHeight : info.geometry?.scrollWidth;

  // Concise geometry summary
  let geomSummary = '';
  if (scrollDim !== undefined && clientDim !== undefined) {
    const fits = maxPos === undefined || maxPos <= 0;
    geomSummary = fits
      ? ` — content (${scrollDim}px) fits the viewport (${clientDim}px); nothing to scroll`
      : ` — at ${axis}=${Math.round(currentPos)}/${maxPos}, content=${scrollDim}px, viewport=${clientDim}px`;
  }

  // Which edge(s) we're pinned to
  const edges: string[] = [];
  if (info.direction === 'down' && info.atBottom) edges.push('already at bottom');
  if (info.direction === 'up' && info.atTop) edges.push('already at top');
  if (info.direction === 'right' && info.atRight) edges.push('already at right edge');
  if (info.direction === 'left' && info.atLeft) edges.push('already at left edge');
  const edgeNote = edges.length ? ` — ${edges.join(', ')}` : '';

  return [
    `Scroll: ${label} → NO MOVEMENT (stayed at ${axis}=${Math.round(currentPos)})${geomSummary}${edgeNote}.`,
    `The page did not change; no new snapshot returned.`,
    ``,
    `If you expected more content this direction, try one of:`,
    `  • browser_screenshot({ fullPage: true, returnImage: true }) — re-orient with the whole page at once; the element you want may already be in view, elsewhere, or not on this page`,
    `  • toRef=<target-ref> — jump directly to a known element`,
    `  • withinRef=<ancestor-ref> — if a nested list/dialog/grid has its own scroller, target it`,
    ``,
    `Do NOT retry the same ${info.direction} scroll — it will stay at ${axis}=${Math.round(currentPos)}.`,
  ].join('\n');
}
